import { useMemo } from 'react';

import {
  ArtificialIntelligence08Icon,
  Image01Icon,
  LayoutTopIcon,
  PaintBoardIcon,
  Pdf01Icon,
  TextFontIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Controller, useWatch } from 'react-hook-form';

import BrandingTabSplitLayout from 'components/BrandingTabSplitLayout/BrandingTabSplitLayout';
import { ColorContrastCard } from 'components/ColorContrastCard/ColorContrastCard';
import DisplayBox from 'components/DisplayBox/DisplayBox';
import { ColorInput, ImagePicker, InputGroup, Toggle } from 'components/FormComponents';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { IconComponent } from 'components/Icon/IconComponent';

import { useGetUploadQuery } from 'api/upload-api';

import { ReportingPreview } from './ReportingPreview';

const REPORT_COLOR_DEFAULTS = {
  report_header_bgcolor: '#2D264D',
  report_header_color: '#FFFFFF',
  report_primary_text_color: '#4E18BB',
  report_secondary_color: '#FFFFFF',
  report_bg_color: '#4E18BB',
};

export const Reporting = ({
  brand,
  control,
  register,
  readOnly = false,
  companyChecks = [],
  backgroundColor = 'background-secondary',
}) => {
  const reportBannerId = useWatch({ control, name: 'report_banner_id' });
  const reportHeaderBgcolorValue = useWatch({ control, name: 'report_header_bgcolor' });
  const reportHeaderColorValue = useWatch({ control, name: 'report_header_color' });
  const reportPrimaryTextColorValue = useWatch({ control, name: 'report_primary_text_color' });
  const reportSecondaryColorValue = useWatch({ control, name: 'report_secondary_color' });
  const reportBgColorValue = useWatch({ control, name: 'report_bg_color' });
  const aiSummaryEnabledValue = useWatch({ control, name: 'ff_ai_reference_summary' });
  const reportHeaderValue = useWatch({ control, name: 'report_header' });

  const hasAISummaryConfigEnabled = useMemo(
    () => companyChecks.some(companyCheck => companyCheck.configuration?.ai_summary === true),
    [companyChecks],
  );

  const { data: reportBannerUpload } = useGetUploadQuery(reportBannerId, { skip: !reportBannerId });

  const previewBrand = useMemo(() => {
    if (!brand) return null;
    const hasBannerId = reportBannerId != null && reportBannerId !== '';
    const resolvedReportBanner = !hasBannerId
      ? null
      : (reportBannerUpload?.url ??
        (String(reportBannerId) === String(brand.report_banner_id)
          ? brand.report_banner
          : undefined));

    return {
      ...brand,
      report_banner_id: reportBannerId,
      report_banner: resolvedReportBanner,
      report_header: reportHeaderValue ?? true,
      report_header_bgcolor:
        reportHeaderBgcolorValue || REPORT_COLOR_DEFAULTS.report_header_bgcolor,
      report_header_color: reportHeaderColorValue || REPORT_COLOR_DEFAULTS.report_header_color,
      report_primary_text_color:
        reportPrimaryTextColorValue || REPORT_COLOR_DEFAULTS.report_primary_text_color,
      report_secondary_color:
        reportSecondaryColorValue || REPORT_COLOR_DEFAULTS.report_secondary_color,
      report_bg_color: reportBgColorValue || REPORT_COLOR_DEFAULTS.report_bg_color,
      ff_ai_reference_summary: aiSummaryEnabledValue || false,
    };
  }, [
    brand,
    reportBannerId,
    reportBannerUpload,
    reportHeaderBgcolorValue,
    reportHeaderColorValue,
    reportPrimaryTextColorValue,
    reportSecondaryColorValue,
    reportBgColorValue,
    aiSummaryEnabledValue,
    reportHeaderValue,
  ]);

  return (
    <BrandingTabSplitLayout
      key={brand?.id}
      shellClassName={backgroundColor}
      noPadding
      previewTitle="Report Preview"
      previewHeaderExtra="This is a sample preview. The original report will have more content."
      preview={previewBrand ? <ReportingPreview brand={previewBrand} /> : null}>
      <div className="flex flex-col gap-8">
        <FormMessage
          message="Branding for PDF reports currently <b>applies only to Reference Check Plus reports and Candidate Questionnaire reports.</b> Existing branding and layouts for all other reports/results will remain unchanged."
          type={MessageType.Info}
          closeable={false}
          noMargin
        />
        <section className="rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between">
              <div className="flex w-3/4 flex-grow-1 gap-2">
                <HugeiconsIcon
                  icon={ArtificialIntelligence08Icon}
                  size={24}
                  className="shrink-0 text-neutral-6"
                />
                <div className="mb-4 flex flex-col gap-2">
                  <span className="text-2xl font-medium text-neutral-7">Report Header</span>
                  <p className="text-xl text-text-tertiary">
                    This will display the report header on the top of the report.
                  </p>
                </div>
              </div>
              <div className="w-auto flex-shrink-0">
                <InputGroup noMargin>
                  <Toggle register={register} name="report_header" small />
                </InputGroup>
              </div>
            </div>
          </div>
          {(reportHeaderValue ?? true) && (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <HugeiconsIcon icon={Image01Icon} size="24" className="shrink-0 text-neutral-6" />
                <div className="flex flex-col gap-2 mb-4">
                  <span className="text-2xl font-medium text-neutral-7">PDF Report banner</span>
                  <p className="text-xl text-text-tertiary">
                    Upload your banner image in PNG, JPG, or SVG format (max 5MB)
                  </p>
                </div>
              </div>
              <InputGroup noMargin>
                <Controller
                  control={control}
                  name="report_banner_id"
                  render={({ field: { onChange, value } }) => (
                    <ImagePicker
                      previewAsCover
                      onChange={onChange}
                      value={value}
                      cropAspect={794 / 300}
                      cropRecommendedSize="794×300px"
                    />
                  )}
                />
              </InputGroup>
            </div>
          )}
          {hasAISummaryConfigEnabled && (
            <div className="flex items-start justify-between">
              <div className="flex w-3/4 flex-grow-1 gap-2">
                <HugeiconsIcon
                  icon={ArtificialIntelligence08Icon}
                  size={24}
                  className="shrink-0 text-neutral-6"
                />
                <div className="mb-4 flex flex-col gap-2">
                  <span className="text-2xl font-medium text-neutral-7">AI Reference Summary</span>
                  <p className="text-xl text-text-tertiary">
                    Enable AI-generated reference summaries for your reference check plus reports.
                  </p>
                </div>
              </div>
              <div className="w-auto flex-shrink-0">
                <InputGroup noMargin>
                  <Toggle register={register} name="ff_ai_reference_summary" small />
                </InputGroup>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <HugeiconsIcon icon={PaintBoardIcon} size="24" className="shrink-0 text-neutral-6" />
              <div className="flex flex-col gap-2 mb-4">
                <span className="text-2xl font-medium text-neutral-7">PDF Report brand colors</span>
                <p className="text-xl text-text-tertiary">
                  Define your report&apos;s color palette
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-4 border border-neutral-4 rounded-3xl p-4">
                <div className="flex gap-2">
                  <IconComponent icon={LayoutTopIcon} size="medium" type="secondary" />
                  <div className="flex flex-col gap-1">
                    <span className="text-2xl font-medium text-neutral-7">PDF Report banner</span>
                    <p className="text-xl text-text-tertiary">
                      These colors are used in the top banner area of your reports, including the
                      report title and background.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <InputGroup title="Report banner background" noMargin>
                    <ColorInput
                      name="report_header_bgcolor"
                      register={register}
                      initialColor={
                        reportHeaderBgcolorValue || REPORT_COLOR_DEFAULTS.report_header_bgcolor
                      }
                    />
                  </InputGroup>
                  <InputGroup title="Report banner text" noMargin>
                    <ColorInput
                      name="report_header_color"
                      register={register}
                      initialColor={
                        reportHeaderColorValue || REPORT_COLOR_DEFAULTS.report_header_color
                      }
                    />
                  </InputGroup>
                </div>
                <DisplayBox show white alignLeft>
                  <ColorContrastCard
                    foreground={reportHeaderColorValue || REPORT_COLOR_DEFAULTS.report_header_color}
                    background={
                      reportHeaderBgcolorValue || REPORT_COLOR_DEFAULTS.report_header_bgcolor
                    }
                    hideDescription
                  />
                </DisplayBox>
              </div>
              <div className="flex flex-col gap-4 border border-neutral-4 rounded-3xl p-4">
                <div className="flex gap-2">
                  <IconComponent icon={TextFontIcon} size="medium" type="secondary" />
                  <div className="flex flex-col gap-1">
                    <span className="text-2xl font-medium text-neutral-7">PDF Display Text</span>
                    <p className="text-xl text-text-tertiary">
                      These colors are used in your report content for display text.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <InputGroup title="Report primary text" noMargin>
                    <ColorInput
                      name="report_primary_text_color"
                      register={register}
                      initialColor={
                        reportPrimaryTextColorValue ||
                        REPORT_COLOR_DEFAULTS.report_primary_text_color
                      }
                    />
                  </InputGroup>
                </div>
                <DisplayBox show white alignLeft>
                  <ColorContrastCard
                    foreground={
                      reportPrimaryTextColorValue || REPORT_COLOR_DEFAULTS.report_primary_text_color
                    }
                    background="#f5f8fd"
                    hideDescription
                  />
                </DisplayBox>
              </div>
              <div className="flex flex-col gap-4 border border-neutral-4 rounded-3xl p-4">
                <div className="flex gap-2">
                  <IconComponent icon={PaintBoardIcon} size="medium" type="secondary" />
                  <div className="flex flex-col gap-1">
                    <span className="text-2xl font-medium text-neutral-7">PDF Report page</span>
                    <p className="text-xl text-text-tertiary">
                      These colors are used for elements throughout the PDF, such as tables,
                      highlights, charts and key visual elements.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <InputGroup title="Report page background" noMargin>
                    <ColorInput
                      name="report_bg_color"
                      register={register}
                      initialColor={reportBgColorValue || REPORT_COLOR_DEFAULTS.report_bg_color}
                    />
                  </InputGroup>
                  <InputGroup title="Report page text" noMargin>
                    <ColorInput
                      name="report_secondary_color"
                      register={register}
                      initialColor={
                        reportSecondaryColorValue || REPORT_COLOR_DEFAULTS.report_secondary_color
                      }
                    />
                  </InputGroup>
                </div>
                <DisplayBox show white alignLeft>
                  <ColorContrastCard
                    foreground={
                      reportSecondaryColorValue || REPORT_COLOR_DEFAULTS.report_secondary_color
                    }
                    background={reportBgColorValue || REPORT_COLOR_DEFAULTS.report_bg_color}
                    hideDescription
                  />
                </DisplayBox>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between">
              <div className="flex w-3/4 flex-grow-1 gap-2">
                <HugeiconsIcon
                  icon={ArtificialIntelligence08Icon}
                  size={24}
                  className="shrink-0 text-neutral-6"
                />
                <div className="mb-4 flex flex-col gap-2">
                  <span className="text-2xl font-medium text-neutral-7">Allow page break</span>
                  <p className="text-xl text-text-tertiary">Enable page break for your reports.</p>
                </div>
              </div>
              <div className="w-auto flex-shrink-0">
                <InputGroup noMargin>
                  <Toggle register={register} name="allow_page_break" small />
                </InputGroup>
              </div>
            </div>
          </div>
        </section>
      </div>
    </BrandingTabSplitLayout>
  );
};
